import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { MenuIcon, X, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, getLocalizedPath } = useLanguage();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  
  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };
  
  const handleLanguageChange = (lang: 'en' | 'fr') => {
    changeLanguage(lang);
    setIsLanguageDropdownOpen(false);
    closeMenu();
  };
  
  // Check scroll position to apply styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Navigation items
  const navItems = [
    { name: t('navigation.home'), path: '/' },
    { name: t('navigation.services'), path: '/services' },
    { name: t('navigation.pricing'), path: '/pricing' },
    { name: t('navigation.about'), path: '/about' },
    { name: t('navigation.contact'), path: '/contact' },
    { name: t('navigation.faq'), path: '/faq' },
  ];
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        {/* Logo */}
        <Link 
          to={getLocalizedPath('/')}
          className="flex items-center" 
          onClick={closeMenu}
        >
          <img src="/logo.png" alt="logo" className="w-20 h-20" />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {navItems.map((item) => {
            const localizedPath = getLocalizedPath(item.path);
            const isActive = location.pathname === localizedPath;
            
            return (
              <Link
                key={item.path}
                to={localizedPath}
                className={`font-medium hover:text-primary transition-colors ${
                  isActive ? 'text-primary' : 'text-gray-700'
                }`}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>
        
        {/* Desktop Language Switcher & CTA */}
        <div className="hidden md:flex items-center space-x-4">
          <div className="relative">
            <button
              onClick={toggleLanguageDropdown}
              className="flex items-center space-x-1 text-gray-700 hover:text-primary"
            >
              <span>{currentLanguage === 'en' ? 'EN' : 'FR'}</span>
              <ChevronDown size={16} />
            </button>
            
            {isLanguageDropdownOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg overflow-hidden z-10 border border-gray-200">
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                    currentLanguage === 'en' ? 'bg-gray-100' : ''
                  }`}
                >
                  {t('languages.en')}
                </button>
                <button
                  onClick={() => handleLanguageChange('fr')}
                  className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                    currentLanguage === 'fr' ? 'bg-gray-100' : ''
                  }`}
                >
                  {t('languages.fr')}
                </button>
              </div>
            )}
          </div>
          
          <Button 
            as={Link} 
            to={getLocalizedPath('/contact')} 
            variant="primary"
            size="sm"
          >
            {t('hero.cta')}
          </Button>
        </div>
        
        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-gray-700 hover:text-primary"
          onClick={toggleMenu}
        >
          {isMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg overflow-hidden animate-slide-down">
          <div className="container-custom py-5">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => {
                const localizedPath = getLocalizedPath(item.path);
                const isActive = location.pathname === localizedPath;
                
                return (
                  <Link
                    key={item.path}
                    to={localizedPath}
                    className={`font-medium hover:text-primary transition-colors py-2 ${
                      isActive ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={closeMenu}
                  >
                    {item.name}
                  </Link>
                );
              })}
              
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{t('languages.en')}</span>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => handleLanguageChange('en')}
                      className={`px-3 py-1 rounded-md ${
                        currentLanguage === 'en' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => handleLanguageChange('fr')}
                      className={`px-3 py-1 rounded-md ${
                        currentLanguage === 'fr' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      FR
                    </button>
                  </div>
                </div>
              </div>
              
              <Button 
                as={Link} 
                to={getLocalizedPath('/contact')}
                variant="primary"
                fullWidth
                className="mt-4"
                onClick={closeMenu}
              >
                {t('hero.cta')}
              </Button>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;